import React, { useState, useEffect, useCallback, useMemo } from "react";
import { X, CheckCircle2, RotateCw, Building2, School, Users } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import { toast } from "react-toastify";
import customSelectStyles from "../../../../components/ui/customSelectStyles";
import {
  customerInModalSelectStyles,
  formatCustomerOptionLabel,
  filterCustomerOption,
  modalSelectPortalProps,
} from "../../../../components/ui/customerSelectInModal";
import { getAllBranches_Array, getBranchById } from "../../../../api/hrm/branches/info";
import HrmDashboardBranchPicker, {
  branchSubtitle,
} from "../../../hrm/shared/hrmDashboardBranchSelect";
import {
  getCertRequestSchools,
  getCertRequestClasses,
  getCertRequestClassStudents,
  createCertificateRequest,
} from "../../../../api/elearning/certificates/certificateRequests";

function readUserBranchId() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u.branch?.id || u.branch_id || null;
  } catch {
    return null;
  }
}

const SESSION_SUPER_ROLES = [
  "super admin",
  "superadmin",
  "administrator",
  "system administrator",
];

function readSessionRoles() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    const roles = Array.isArray(u.roles) ? u.roles : [];
    return roles.map((r) => String(r || "").trim().toLowerCase());
  } catch {
    return [];
  }
}

function isSuperAdminSession() {
  const roles = readSessionRoles();
  return roles.some((r) => SESSION_SUPER_ROLES.includes(r));
}

const CertificateRequestModal = ({ isOpen, onClose, onSuccess }) => {
  const getClassStudentCount = (c) =>
    c?.student_total_count ?? c?.student_active_count ?? c?.student_count ?? c?.total_students;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [branchAffiliation, setBranchAffiliation] = useState("affiliated");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [branchFilterId, setBranchFilterId] = useState("");

  const [unit, setUnit] = useState("school");
  const [schoolId, setSchoolId] = useState("");
  const [classId, setClassId] = useState("");
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (isOpen) {
      const bid = readUserBranchId();
      const isSuper = isSuperAdminSession();
      setIsSuperAdmin(isSuper);
      setBranchId(bid);
      setBranchAffiliation("affiliated");
      setBranchFilterId("");
      setBranchOptions([]);
      setUnit(isSuper ? "school" : "center");
      setSchoolId("");
      setClassId("");
      setStudents([]);
      setSelected({});
      setLoading(true);
      const tasks = [];
      if (isSuper) {
        tasks.push(
          getAllBranches_Array({ is_active: true, limit: 500 }).then((rows) => {
            const opts = (rows || []).map((b) => ({
              value: b.id,
              label: b.name,
              affiliation_type: b.affiliation_type || "affiliated",
              manager: b.manager || "",
            }));
            setBranchOptions(opts);
          })
        );
      } else if (bid) {
        tasks.push(
          getBranchById(bid).then((br) => {
            const aff = br?.affiliation_type || "affiliated";
            setBranchAffiliation(aff);
            setUnit(aff === "franchise" ? "center" : "school");
          })
        );
      }
      Promise.all(tasks)
        .catch(() => {
          setSchools([]);
          setBranchOptions([]);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  const loadClasses = useCallback(async () => {
    const effectiveBranchId = isSuperAdmin ? branchFilterId : branchId;
    if (!effectiveBranchId) return;
    setLoading(true);
    try {
      const params = { branch_id: effectiveBranchId };
      if (unit === "center") {
        params.unit = "center";
      } else if (schoolId) {
        params.school_id = schoolId;
      }
      const d = await getCertRequestClasses(params);
      const incomingClasses = Array.isArray(d?.classes) ? d.classes : [];
      const classesWithStudents = incomingClasses.filter(
        (c) => Number(getClassStudentCount(c) || 0) > 0
      );
      setClasses(classesWithStudents);
    } catch {
      setClasses([]);
      toast.error("Không tải được danh sách lớp.");
    } finally {
      setLoading(false);
    }
  }, [branchId, branchFilterId, isSuperAdmin, unit, schoolId]);

  const loadSchools = useCallback(async () => {
    const effectiveBranchId = isSuperAdmin ? branchFilterId : branchId;
    if (!effectiveBranchId || unit !== "school") {
      setSchools([]);
      return;
    }
    setLoading(true);
    try {
      const d = await getCertRequestSchools(effectiveBranchId);
      setSchools(d.schools || []);
    } catch {
      setSchools([]);
      toast.error("Không tải được danh sách đối tác.");
    } finally {
      setLoading(false);
    }
  }, [branchId, branchFilterId, isSuperAdmin, unit]);

  useEffect(() => {
    if (!isOpen) return;
    if (isSuperAdmin && !branchFilterId) {
      setSchools([]);
      setClasses([]);
      setSchoolId("");
      setClassId("");
      return;
    }
    loadSchools();
  }, [isOpen, isSuperAdmin, branchFilterId, unit, loadSchools]);

  useEffect(() => {
    const effectiveBranchId = isSuperAdmin ? branchFilterId : branchId;
    if (!isOpen || !effectiveBranchId) return;
    if (unit === "school" && !schoolId) {
      setClasses([]);
      setClassId("");
      return;
    }
    loadClasses();
  }, [isOpen, branchId, branchFilterId, isSuperAdmin, unit, schoolId, loadClasses]);

  const loadStudents = useCallback(async () => {
    if (!classId) {
      setStudents([]);
      setSelected({});
      return;
    }
    setLoading(true);
    try {
      const d = await getCertRequestClassStudents(classId);
      setStudents(d.students || []);
      const init = {};
      (d.students || []).forEach((s) => {
        if (s.eligible) init[s.student_id] = true;
      });
      setSelected(init);
    } catch {
      setStudents([]);
      toast.error("Không tải được danh sách học viên.");
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    if (!isOpen || !classId) return;
    loadStudents();
  }, [isOpen, classId, loadStudents]);

  const toggle = (studentId, eligible) => {
    if (!eligible) return;
    setSelected((prev) => ({ ...prev, [studentId]: !prev[studentId] }));
  };

  const splitName = (fullName) => {
    const cleaned = String(fullName || "").trim().replace(/\s+/g, " ");
    if (!cleaned) return { lastName: "", firstName: "" };
    const parts = cleaned.split(" ");
    const firstName = parts.pop() || "";
    const lastName = parts.join(" ");
    return { lastName, firstName };
  };

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]),
    [selected]
  );
  const sortedStudents = useMemo(() => {
    const arr = [...(students || [])];
    arr.sort((a, b) => {
      const an = splitName(a.full_name);
      const bn = splitName(b.full_name);
      const byFirst = an.firstName.localeCompare(bn.firstName, "vi", { sensitivity: "base" });
      if (byFirst !== 0) return byFirst;
      const byLast = an.lastName.localeCompare(bn.lastName, "vi", { sensitivity: "base" });
      if (byLast !== 0) return byLast;
      return String(a.student_id || "").localeCompare(String(b.student_id || ""), "vi", {
        sensitivity: "base",
      });
    });
    return arr;
  }, [students]);
  const eligibleIds = useMemo(
    () => sortedStudents.filter((s) => s.eligible).map((s) => s.student_id),
    [sortedStudents]
  );
  const allEligibleSelected =
    eligibleIds.length > 0 && eligibleIds.every((id) => Boolean(selected[id]));
  const someEligibleSelected = eligibleIds.some((id) => Boolean(selected[id]));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = { ...prev };
      if (allEligibleSelected) {
        eligibleIds.forEach((id) => {
          next[id] = false;
        });
      } else {
        eligibleIds.forEach((id) => {
          next[id] = true;
        });
      }
      return next;
    });
  };
  const schoolOptions = useMemo(
    () =>
      schools.map((s) => ({
        value: s.id,
        label: s.name,
        ward: s.ward || s.ward_name || "",
      })),
    [schools]
  );
  const classOptions = useMemo(
    () =>
      classes.map((c) => ({
        value: c.id,
        label: `${c.name}${c.course_name ? ` — ${c.course_name}` : ""}`,
      })),
    [classes]
  );
  const classSignal = useMemo(() => {
    const total = classOptions.length;
    if (unit === "school") {
      const selectedPartner = schoolOptions.find((opt) => opt.value === schoolId);
      return {
        total,
        label: selectedPartner
          ? `Lớp học khớp với đối tác "${selectedPartner.label}": ${total}`
          : `Lớp học khớp theo điều kiện hiện tại: ${total}`,
      };
    }
    return {
      total,
      label: `Lớp học tại trung tâm khớp điều kiện: ${total}`,
    };
  }, [classOptions, unit, schoolId, schoolOptions]);
  const schoolSignal = useMemo(
    () => ({
      matchedSchoolCount: schoolOptions.length,
      branchTotalCount: schoolOptions.length,
    }),
    [schoolOptions]
  );
  const branchSelectOptions = useMemo(
    () => [
      { value: "", label: "Chọn chi nhánh", sub: "Tên chi nhánh • Loại chi nhánh • Người đứng đầu" },
      ...branchOptions.map((b) => ({
        value: b.value,
        label: b.label,
        sub: branchSubtitle(b),
      })),
    ],
    [branchOptions]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const effectiveBranchId = isSuperAdmin ? branchFilterId : branchId;
    if (!effectiveBranchId) {
      toast.error(isSuperAdmin ? "Vui lòng chọn chi nhánh." : "Không xác định được chi nhánh. Vui lòng đăng nhập lại.");
      return;
    }
    if (!classId) {
      toast.error("Chọn lớp học.");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Chọn ít nhất một học viên đã có Kết quả để in giấy chứng nhận.");
      return;
    }
    setSubmitting(true);
    try {
      await createCertificateRequest({
        class_id: classId,
        school_id: unit === "school" ? schoolId || undefined : undefined,
        student_ids: selectedIds,
      });
      toast.success("Đã gửi yêu cầu cấp chứng nhận.");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Không thể gửi yêu cầu.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white w-full max-w-5xl rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[92vh] relative z-[110]"
        >
          <div className="p-6 border-b border-red-100/70 sticky top-0 bg-gradient-to-r from-white via-red-50/35 to-white z-10 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-black text-gray-800 tracking-tight">
                Yêu cầu <span className="text-red-700">cấp giấy chứng nhận</span>
              </h2>
              <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mt-1">
                Chi nhánh gửi yêu cầu · Phòng chuyên môn duyệt · Lãnh đạo phê duyệt
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2.5 hover:bg-white rounded-full text-gray-400 hover:text-gray-600 transition-colors border border-gray-100"
            >
              <X size={22} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-[#fafbfc]">
            {loading && !students.length && !classes.length ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="space-y-4">
                {!isSuperAdmin && !branchId ? (
                  <div className="text-sm text-amber-700 font-bold bg-amber-50 border border-amber-100 rounded-2xl p-3.5">
                    Tài khoản chưa gắn chi nhánh. Không thể gửi yêu cầu.
                  </div>
                ) : null}

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                  <div className="xl:col-span-2 bg-white border border-gray-100 rounded-2xl p-4 md:p-5 space-y-4 shadow-sm">
                    {isSuperAdmin ? (
                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                          Chi nhánh
                        </label>
                        <HrmDashboardBranchPicker
                          instanceId="cert-request-branch"
                          options={branchSelectOptions}
                          value={branchFilterId || ""}
                          onChange={(nextVal) => {
                            const nextBranchId = nextVal || "";
                            const picked = branchOptions.find((b) => b.value === nextBranchId);
                            const aff = picked?.affiliation_type || "affiliated";
                            setBranchFilterId(nextBranchId);
                            setBranchAffiliation(aff);
                            setUnit(aff === "franchise" ? "center" : "school");
                            setSchoolId("");
                            setClassId("");
                            setStudents([]);
                            setSelected({});
                          }}
                          aria-label="Chọn chi nhánh yêu cầu cấp chứng nhận"
                        />
                      </div>
                    ) : null}

                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                        Đối tượng
                      </label>
                      <div className="grid grid-cols-2 gap-3 pt-1">
                        {branchAffiliation !== "franchise" ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setUnit("school");
                                setClasses([]);
                                setStudents([]);
                                setSelected({});
                                setSchoolId("");
                                setClassId("");
                              }}
                              className={`flex flex-col items-center p-4 rounded-2xl border-2 transition-all ${
                                unit === "school"
                                  ? "border-red-600 bg-red-50 text-red-700 shadow-lg shadow-red-100"
                                  : "border-gray-100 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              <span className="text-2xl mb-1.5">🏫</span>
                              <span className="text-[11px] font-black uppercase tracking-wider">Tại trường học</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setUnit("center");
                                setClasses([]);
                                setStudents([]);
                                setSelected({});
                                setSchoolId("");
                                setClassId("");
                              }}
                              className={`flex flex-col items-center p-4 rounded-2xl border-2 transition-all ${
                                unit === "center"
                                  ? "border-red-600 bg-red-50 text-red-700 shadow-lg shadow-red-100"
                                  : "border-gray-100 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              <span className="text-2xl mb-1.5">🏛️</span>
                              <span className="text-[11px] font-black uppercase tracking-wider">Tại trung tâm</span>
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="col-span-2 flex flex-col items-center p-4 rounded-2xl border-2 border-red-600 bg-red-50 text-red-700 shadow-lg shadow-red-100"
                            disabled
                          >
                            <span className="text-2xl mb-1.5">🏛️</span>
                            <span className="text-[11px] font-black uppercase tracking-wider">Tại trung tâm</span>
                          </button>
                        )}
                      </div>
                    </div>

                    {unit === "school" ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2">
                          <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                            Chọn đối tác
                          </label>
                          <Select
                            options={schoolOptions}
                            value={schoolOptions.find((opt) => opt.value === schoolId) || null}
                            onChange={(opt) => {
                              setSchoolId(opt?.value || "");
                              setClasses([]);
                              setStudents([]);
                              setSelected({});
                              setClassId("");
                            }}
                            placeholder="Chọn đối tác trường học *"
                            styles={customerInModalSelectStyles}
                            formatOptionLabel={formatCustomerOptionLabel}
                            filterOption={filterCustomerOption}
                            {...modalSelectPortalProps}
                          />
                          <p className="mt-1 text-[10px] font-semibold tracking-wide text-gray-500">
                            {`Khớp loại trường: ${schoolSignal.matchedSchoolCount} | Tổng đối tác chi nhánh: ${schoolSignal.branchTotalCount}`}
                          </p>
                        </div>
                      </motion.div>
                    ) : null}

                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                        <Users size={14} /> Lớp học
                      </label>
                      <Select
                        options={classOptions}
                        value={classOptions.find((opt) => opt.value === classId) || null}
                        onChange={(opt) => setClassId(opt?.value || "")}
                        placeholder="Chọn lớp học *"
                        isDisabled={
                          (isSuperAdmin && !branchFilterId) ||
                          (unit === "school" && !schoolId)
                        }
                        styles={customSelectStyles}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      />
                      <p className="mt-1 text-[10px] font-semibold tracking-wide text-gray-500">
                        {classSignal.label}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-100 rounded-2xl p-4 md:p-5 shadow-sm flex flex-col gap-3">
                    <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Tóm tắt yêu cầu</p>
                    <div className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2">
                      <span className="text-xs font-bold text-gray-500 uppercase">Đối tượng</span>
                      <span className="text-xs font-black text-gray-700">
                        {unit === "school" ? "Tại trường" : "Tại trung tâm"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2">
                      <span className="text-xs font-bold text-gray-500 uppercase">Đã chọn lớp</span>
                      <span className="text-xs font-black text-gray-700">{classId ? "Có" : "Chưa"}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-red-50 border border-red-100 px-3 py-2">
                      <span className="text-xs font-bold text-red-600 uppercase">Học viên đã chọn</span>
                      <span className="text-sm font-black text-red-700">{selectedIds.length}</span>
                    </div>
                    <p className="text-[11px] leading-relaxed text-gray-500">
                      Hệ thống chỉ cho phép chọn học viên đã có <span className="font-bold text-gray-700">Kết quả</span> để in trên giấy chứng nhận.
                    </p>
                  </div>
                </div>

                {classId ? (
                  <div className="border border-gray-100 rounded-2xl overflow-hidden bg-white shadow-sm">
                      <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-3 text-[11px] font-black text-gray-500 uppercase tracking-widest flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span>Danh sách học viên đủ điều kiện</span>
                        <button
                          type="button"
                          onClick={() => {
                            if (allEligibleSelected) return;
                            toggleSelectAll();
                          }}
                          disabled={eligibleIds.length === 0 || allEligibleSelected}
                          className="px-2 py-1 rounded-md border border-gray-200 bg-white text-[10px] font-black text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Chọn tất cả
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!someEligibleSelected) return;
                            if (allEligibleSelected) toggleSelectAll();
                            else {
                              setSelected((prev) => {
                                const next = { ...prev };
                                eligibleIds.forEach((id) => {
                                  next[id] = false;
                                });
                                return next;
                              });
                            }
                          }}
                          disabled={eligibleIds.length === 0 || !someEligibleSelected}
                          className="px-2 py-1 rounded-md border border-gray-200 bg-white text-[10px] font-black text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Bỏ chọn tất cả
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-red-700">Đã chọn: {selectedIds.length}/{eligibleIds.length}</span>
                      </div>
                    </div>
                    {sortedStudents.length > 0 ? (
                      <div className="max-h-80 overflow-auto">
                        <table className="w-full min-w-[1200px] text-sm">
                          <thead className="bg-gray-50 text-gray-500">
                            <tr>
                              <th className="px-3 py-2 text-center font-semibold w-12 sticky top-0 left-0 z-40 bg-gray-50">
                                <input
                                  type="checkbox"
                                  checked={allEligibleSelected}
                                  ref={(el) => {
                                    if (el) el.indeterminate = !allEligibleSelected && someEligibleSelected;
                                  }}
                                  onChange={toggleSelectAll}
                                  className="rounded border-gray-300 text-red-700 focus:ring-red-500"
                                  aria-label={allEligibleSelected ? "Bỏ chọn tất cả" : "Chọn tất cả"}
                                />
                              </th>
                              <th className="px-3 py-2 text-center font-semibold w-14 sticky top-0 left-12 z-40 bg-gray-50">STT</th>
                              <th className="px-3 py-2 text-left font-semibold min-w-[150px] sticky top-0 left-[68px] z-40 bg-gray-50">Họ</th>
                              <th className="px-3 py-2 text-left font-semibold min-w-[130px] sticky top-0 left-[218px] z-40 bg-gray-50 border-r border-gray-200">Tên</th>
                              <th className="px-3 py-2 text-left font-semibold min-w-[110px] sticky top-0 z-30 bg-gray-50">Ngày sinh</th>
                              <th className="px-3 py-2 text-left font-semibold min-w-[90px] sticky top-0 z-30 bg-gray-50">Giới tính</th>
                              <th className="px-3 py-2 text-center font-semibold min-w-[80px] sticky top-0 z-30 bg-gray-50">Đầu vào</th>
                              <th className="px-3 py-2 text-center font-semibold min-w-[80px] sticky top-0 z-30 bg-gray-50">Đầu ra</th>
                              <th className="px-3 py-2 text-left font-semibold min-w-[220px] sticky top-0 z-30 bg-gray-50">Kết quả</th>
                              <th className="px-3 py-2 text-left font-semibold min-w-[220px] sticky top-0 z-30 bg-gray-50">Đánh giá</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {sortedStudents.map((s, idx) => {
                              const n = splitName(s.full_name);
                              return (
                                <tr
                                  key={s.student_id}
                                  className={s.eligible ? "hover:bg-red-50/30" : "opacity-60 bg-gray-50/40"}
                                >
                                  <td className="px-3 py-2 text-center sticky left-0 z-10 bg-white">
                                    <input
                                      type="checkbox"
                                      checked={!!selected[s.student_id]}
                                      disabled={!s.eligible}
                                      onChange={() => toggle(s.student_id, s.eligible)}
                                      className="rounded border-gray-300 text-red-700 focus:ring-red-500"
                                      aria-label={`Chọn học viên ${s.full_name || s.student_id}`}
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-center text-xs text-gray-500 sticky left-12 z-10 bg-white">{idx + 1}</td>
                                  <td className="px-3 py-2 text-gray-700 sticky left-[68px] z-10 bg-white">{n.lastName || "—"}</td>
                                  <td className="px-3 py-2 font-semibold text-gray-800 sticky left-[218px] z-10 bg-white border-r border-gray-100">{n.firstName || "—"}</td>
                                  <td className="px-3 py-2 text-gray-600">{s.dob || "—"}</td>
                                  <td className="px-3 py-2 text-gray-600">
                                    {s.gender === "male" ? "Nam" : s.gender === "female" ? "Nữ" : "—"}
                                  </td>
                                  <td className="px-3 py-2 text-center text-gray-700">
                                    {s.entrance_score != null ? Number(s.entrance_score).toFixed(1) : "—"}
                                  </td>
                                  <td className="px-3 py-2 text-center text-gray-700">
                                    {s.exit_score != null ? Number(s.exit_score).toFixed(1) : "—"}
                                  </td>
                                  <td className="px-3 py-2 text-gray-700">{s.outcome_text || "—"}</td>
                                  <td className="px-3 py-2 text-gray-600">{s.remarks || "—"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="px-4 py-5 text-sm text-gray-500 bg-amber-50/40 border-t border-amber-100">
                        Chưa có học viên đủ dữ liệu để xét chứng nhận cho lớp này. Hệ thống lấy danh sách từ bảng điểm
                        và chỉ cho chọn học viên đã có <span className="font-bold text-gray-700">Kết quả</span>.
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </form>

          <div className="p-5 md:p-6 border-t border-gray-100 bg-white flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition-all uppercase text-xs tracking-wider"
            >
              Đóng
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || loading || !(isSuperAdmin ? branchFilterId : branchId) || !classId || selectedIds.length === 0}
              className="px-8 py-3 bg-red-700 hover:bg-red-800 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg transition-all active:scale-95 uppercase text-xs tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <RotateCw size={18} className="animate-spin" />
              ) : (
                <CheckCircle2 size={18} />
              )}
              {submitting ? "Đang gửi..." : "Gửi yêu cầu"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CertificateRequestModal;
